import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { adminListPackages, adminCreatePackage, adminUpdatePackage, adminDeletePackage, getServices } from '@/lib/api';
import { formatCents, dollarsToCents, centsToDollarsInput } from '@/lib/money';
import { ADMIN_PACKAGES } from '@/constants/testIds';

const NONE = '__none__';
const EMPTY = { name: '', service_slug: NONE, price: '', deposit_percent: '30', duration_label: '', description: '', includes: '' };

const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';
const labelClass = 'text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]';

export const AdminPackagesTab = () => {
  const [packages, setPackages] = useState(null);
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const refresh = () => adminListPackages().then(setPackages).catch(() => setPackages([]));

  useEffect(() => {
    refresh();
    getServices().then(setServices).catch(() => setServices([]));
  }, []);

  const serviceName = (slug) => services.find((s) => s.slug === slug)?.name || slug || '—';
  const isLxra = packages?.length ? packages[0].studio_id === 'lxra' : true;

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (pkg) => {
    setEditing(pkg);
    setForm({
      name: pkg.name,
      service_slug: pkg.service_slug || NONE,
      price: centsToDollarsInput(pkg.price_cents),
      deposit_percent: String(pkg.deposit_percent ?? 30),
      duration_label: pkg.duration_label || '',
      description: pkg.description || '',
      includes: (pkg.includes || []).join('\n'),
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      service_slug: form.service_slug === NONE ? null : form.service_slug,
      price_cents: dollarsToCents(form.price),
      deposit_percent: Math.max(0, Math.min(100, Number.parseInt(form.deposit_percent, 10) || 0)),
      duration_label: form.duration_label.trim(),
      description: form.description.trim(),
      includes: form.includes.split('\n').map((l) => l.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await adminUpdatePackage(editing.id, { ...payload, service_slug: payload.service_slug ?? '' });
        toast.success('Package updated.');
      } else {
        await adminCreatePackage(payload);
        toast.success('Package created.');
      }
      setDialogOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save the package.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pkg) => {
    try {
      const updated = await adminUpdatePackage(pkg.id, { active: !pkg.active });
      setPackages((prev) => prev.map((p) => (p.id === pkg.id ? updated : p)));
      toast.success(updated.active ? `${pkg.name} is bookable again.` : `${pkg.name} hidden from the booking form.`);
    } catch {
      toast.error('Could not update the package.');
    }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.name}"? Existing bookings keep their price.`)) return;
    try {
      await adminDeletePackage(pkg.id);
      setPackages((prev) => prev.filter((p) => p.id !== pkg.id));
      toast.success('Package deleted.');
    } catch {
      toast.error('Could not delete the package.');
    }
  };

  return (
    <div className="space-y-6" data-testid={ADMIN_PACKAGES.section}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))]">LXRA Creator</p>
          <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-1">Packages</h2>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1">What you sell. Active packages appear on the public booking form and pre-fill prices on new bookings.</p>
        </div>
        <Button
          onClick={openCreate}
          data-testid={ADMIN_PACKAGES.newButton}
          className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Package
        </Button>
      </div>

      {!packages ? (
        <Skeleton className="h-64 rounded-2xl bg-[#141416]" />
      ) : packages.length === 0 ? (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-12 text-center">
          <Package className="h-8 w-8 mx-auto text-[hsl(var(--lxra-gold))]" />
          <p className="mt-4 text-sm text-[hsl(var(--lxra-fg))]">No packages yet.</p>
          <p className="mt-1 text-xs text-[hsl(var(--lxra-muted))]">Create your first package so clients can request it and bookings can be priced automatically.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={ADMIN_PACKAGES.table}>
          <Table>
            <TableHeader>
              <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
                <TableHead className="text-[hsl(var(--lxra-muted))]">Package</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))]">Service</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Price</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Deposit</TableHead>
                {!isLxra && <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Your net (after LXRA fee)</TableHead>}
                <TableHead className="text-[hsl(var(--lxra-muted))]">Bookable</TableHead>
                <TableHead className="text-[hsl(var(--lxra-muted))] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((p) => (
                <TableRow key={p.id} data-testid={ADMIN_PACKAGES.row(p.id)} className={`border-[hsl(var(--lxra-border))] hover:bg-[#141416] ${p.active ? '' : 'opacity-60'}`}>
                  <TableCell>
                    <p className="text-[hsl(var(--lxra-fg))]">{p.name}</p>
                    <p className="text-xs text-[hsl(var(--lxra-muted))]">{p.duration_label || `${p.includes?.length || 0} inclusions`}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-muted))] text-[10px] font-normal">{serviceName(p.service_slug)}</Badge>
                  </TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-fg))] font-medium whitespace-nowrap">{formatCents(p.price_cents)}</TableCell>
                  <TableCell className="text-right text-[hsl(var(--lxra-muted))] text-xs whitespace-nowrap">{p.deposit_cents ? `${formatCents(p.deposit_cents)} (${p.deposit_percent ?? 30}%)` : '—'}</TableCell>
                  {!isLxra && <TableCell className="text-right text-[hsl(var(--lxra-gold))] text-xs whitespace-nowrap">{formatCents(Math.round(p.price_cents * 0.8))}</TableCell>}
                  <TableCell>
                    <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={ADMIN_PACKAGES.activeSwitch(p.id)} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={ADMIN_PACKAGES.editButton(p.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10"><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={ADMIN_PACKAGES.deleteButton(p.id)} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] max-w-lg max-h-[90vh] overflow-y-auto" data-testid={ADMIN_PACKAGES.dialog}>
          <DialogHeader>
            <DialogTitle className="text-[hsl(var(--lxra-fg))]">{editing ? 'Edit Package' : 'New Package'}</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">Prices are in USD. The deposit is what you ask for to hold the date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-1.5">
              <Label className={labelClass}>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={ADMIN_PACKAGES.nameInput} className={fieldClass} placeholder="Signature" />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Service</Label>
              <Select value={form.service_slug} onValueChange={(v) => setForm({ ...form, service_slug: v })}>
                <SelectTrigger data-testid={ADMIN_PACKAGES.serviceSelect} className={fieldClass}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
                  <SelectItem value={NONE}>Any / general</SelectItem>
                  {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className={labelClass}>Price ($)</Label>
                <Input inputMode="decimal" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid={ADMIN_PACKAGES.priceInput} className={fieldClass} placeholder="450" />
              </div>
              <div className="space-y-1.5">
                <Label className={labelClass}>Deposit (%)</Label>
                <Input inputMode="numeric" value={form.deposit_percent} onChange={(e) => setForm({ ...form, deposit_percent: e.target.value })} data-testid={ADMIN_PACKAGES.depositInput} className={fieldClass} placeholder="30" />
                <p className="text-[11px] text-[hsl(var(--lxra-muted))]">= {formatCents(Math.round(dollarsToCents(form.price) * (Number.parseInt(form.deposit_percent, 10) || 0) / 100))} due at booking</p>
              </div>
            </div>
            {!isLxra && form.price && (
              <p className="text-[11px] text-[hsl(var(--lxra-muted))] -mt-1">LXRA takes a 20% marketplace fee · you keep {formatCents(Math.round(dollarsToCents(form.price) * 0.8))} of this package's price.</p>
            )}
            <div className="space-y-1.5">
              <Label className={labelClass}>Duration / scope</Label>
              <Input value={form.duration_label} onChange={(e) => setForm({ ...form, duration_label: e.target.value })} data-testid={ADMIN_PACKAGES.durationInput} className={fieldClass} placeholder="2 hours · up to 2 locations" />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={ADMIN_PACKAGES.descriptionTextarea} className={`${fieldClass} h-auto resize-none`} />
            </div>
            <div className="space-y-1.5">
              <Label className={labelClass}>What&apos;s included (one per line)</Label>
              <Textarea rows={4} value={form.includes} onChange={(e) => setForm({ ...form, includes: e.target.value })} data-testid={ADMIN_PACKAGES.includesTextarea} className={`${fieldClass} h-auto resize-y`} placeholder={'60+ edited photos\nOnline private gallery'} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid={ADMIN_PACKAGES.saveButton} className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? 'Save Changes' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
